import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Select,
    InputLabel,
    MenuItem,
    FormControl,
    TextField,
    FormControlLabel,
    Checkbox,
    Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
}));

const SmtpConfigSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Muito curto!")
        .max(50, "Muito longo!")
        .required("Obrigatório"),
    host: Yup.string().required("Obrigatório"),
    port: Yup.number().required("Obrigatório").positive().integer(),
    encryption: Yup.string().required("Obrigatório"),
    username: Yup.string().required("Obrigatório"),
    password: Yup.string().when("$isEditing", {
        is: false,
        then: Yup.string().required("Obrigatório"),
        otherwise: Yup.string(),
    }),
    fromName: Yup.string().required("Obrigatório"),
    fromEmail: Yup.string().email("E-mail inválido").required("Obrigatório"),
});

const SmtpConfigModal = ({ open, onClose, smtpConfigId, onSave }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);

    const initialState = {
        name: "",
        host: "",
        port: 587,
        encryption: "TLS",
        username: "",
        password: "",
        fromName: "",
        fromEmail: "",
        isGlobal: false,
        userId: null,
        isActive: true,
        // Campos IMAP
        imapHost: "",
        imapPort: 993,
        imapEncryption: "SSL",
        imapUsername: "",
        imapPassword: "",
    };

    const [smtpConfig, setSmtpConfig] = useState(initialState);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            if (user.profile === "admin") {
                try {
                    const { data } = await api.get("/users");
                    setUsers(data.users);
                } catch (err) {
                    toastError(err);
                }
            }
        };
        fetchUsers();
    }, [user]);

    useEffect(() => {
        const fetchSmtpConfig = async () => {
            if (!smtpConfigId) return;
            try {
                const { data } = await api.get(`/smtp-configs/${smtpConfigId}`);
                setSmtpConfig(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchSmtpConfig();
    }, [smtpConfigId, open]);

    const handleClose = () => {
        setSmtpConfig(initialState);
        onClose();
    };

    const handleSaveSmtpConfig = async (values) => {
        const smtpConfigData = { ...values };

        // Se não tiver senha (edição), remover do payload
        if (!smtpConfigData.password) {
            delete smtpConfigData.password;
        }

        // Se não tiver senha IMAP (edição), remover do payload
        if (!smtpConfigData.imapPassword) {
            delete smtpConfigData.imapPassword;
        }

        try {
            if (smtpConfigId) {
                await api.put(`/smtp-configs/${smtpConfigId}`, smtpConfigData);
                toast.success("Configuração SMTP atualizada com sucesso!");
            } else {
                await api.post("/smtp-configs", smtpConfigData);
                toast.success("Configuração SMTP criada com sucesso!");
            }
            onSave();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const handleTestConnection = async (values) => {
        try {
            const { data } = await api.post(`/smtp-configs/${smtpConfigId || 0}/test`, values);
            if (data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
            >
                <DialogTitle id="form-dialog-title">
                    {smtpConfigId
                        ? "Editar Configuração SMTP"
                        : "Nova Configuração SMTP"}
                </DialogTitle>
                <Formik
                    initialValues={smtpConfig}
                    enableReinitialize={true}
                    validationSchema={SmtpConfigSchema}
                    context={{ isEditing: !!smtpConfigId }}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveSmtpConfig(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label="Nome da Configuração"
                                            name="name"
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={8}>
                                        <Field
                                            as={TextField}
                                            label="Host SMTP"
                                            name="host"
                                            error={touched.host && Boolean(errors.host)}
                                            helperText={touched.host && errors.host}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            placeholder="smtp.gmail.com"
                                        />
                                    </Grid>

                                    <Grid item xs={4}>
                                        <Field
                                            as={TextField}
                                            label="Porta"
                                            name="port"
                                            type="number"
                                            error={touched.port && Boolean(errors.port)}
                                            helperText={touched.port && errors.port}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            error={touched.encryption && Boolean(errors.encryption)}
                                        >
                                            <InputLabel>Criptografia</InputLabel>
                                            <Field
                                                as={Select}
                                                label="Criptografia"
                                                name="encryption"
                                            >
                                                <MenuItem value="TLS">TLS</MenuItem>
                                                <MenuItem value="SSL">SSL</MenuItem>
                                                <MenuItem value="NONE">Nenhuma</MenuItem>
                                            </Field>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label="Usuário/E-mail"
                                            name="username"
                                            error={touched.username && Boolean(errors.username)}
                                            helperText={touched.username && errors.username}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label={smtpConfigId ? "Senha (deixe em branco para manter)" : "Senha"}
                                            name="password"
                                            type="password"
                                            error={touched.password && Boolean(errors.password)}
                                            helperText={touched.password && errors.password}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label="Nome do Remetente"
                                            name="fromName"
                                            error={touched.fromName && Boolean(errors.fromName)}
                                            helperText={touched.fromName && errors.fromName}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label="E-mail do Remetente"
                                            name="fromEmail"
                                            error={touched.fromEmail && Boolean(errors.fromEmail)}
                                            helperText={touched.fromEmail && errors.fromEmail}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    {/* Seção IMAP */}
                                    <Grid item xs={12}>
                                        <h4 style={{ marginTop: 20, marginBottom: 10 }}>Configurações IMAP (Recebimento)</h4>
                                    </Grid>

                                    <Grid item xs={8}>
                                        <Field
                                            as={TextField}
                                            label="Host IMAP"
                                            name="imapHost"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            placeholder="imap.gmail.com"
                                        />
                                    </Grid>

                                    <Grid item xs={4}>
                                        <Field
                                            as={TextField}
                                            label="Porta IMAP"
                                            name="imapPort"
                                            type="number"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        >
                                            <InputLabel>Criptografia IMAP</InputLabel>
                                            <Field
                                                as={Select}
                                                label="Criptografia IMAP"
                                                name="imapEncryption"
                                            >
                                                <MenuItem value="SSL">SSL</MenuItem>
                                                <MenuItem value="TLS">TLS</MenuItem>
                                                <MenuItem value="NONE">Nenhuma</MenuItem>
                                            </Field>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label="Usuário IMAP"
                                            name="imapUsername"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            placeholder="Geralmente o mesmo do SMTP"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label={smtpConfigId ? "Senha IMAP (deixe em branco para manter)" : "Senha IMAP"}
                                            name="imapPassword"
                                            type="password"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            placeholder="Geralmente a mesma do SMTP"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.isGlobal}
                                                    onChange={(e) => {
                                                        setFieldValue("isGlobal", e.target.checked);
                                                        if (e.target.checked) {
                                                            setFieldValue("userId", null);
                                                        }
                                                    }}
                                                    name="isGlobal"
                                                    color="primary"
                                                />
                                            }
                                            label="Configuração Global (padrão do sistema)"
                                        />
                                    </Grid>

                                    {!values.isGlobal && user.profile === "admin" && (
                                        <Grid item xs={12}>
                                            <FormControl variant="outlined" margin="dense" fullWidth>
                                                <InputLabel>Usuário Específico</InputLabel>
                                                <Field
                                                    as={Select}
                                                    label="Usuário Específico"
                                                    name="userId"
                                                >
                                                    <MenuItem value={null}>
                                                        <em>Selecione um usuário</em>
                                                    </MenuItem>
                                                    {users.map((u) => (
                                                        <MenuItem key={u.id} value={u.id}>
                                                            {u.name} ({u.email})
                                                        </MenuItem>
                                                    ))}
                                                </Field>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.isActive}
                                                    onChange={(e) =>
                                                        setFieldValue("isActive", e.target.checked)
                                                    }
                                                    name="isActive"
                                                    color="primary"
                                                />
                                            }
                                            label="Ativa"
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                {smtpConfigId && (
                                    <Button
                                        onClick={() => handleTestConnection(values)}
                                        color="default"
                                        variant="outlined"
                                    >
                                        Testar Conexão
                                    </Button>
                                )}
                                <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                                    Cancelar
                                </Button>
                                <div className={classes.btnWrapper}>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        disabled={isSubmitting}
                                        variant="contained"
                                    >
                                        Salvar
                                    </Button>
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </div>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default SmtpConfigModal;
